import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { Cell, Label, Row, StatusPayment, Text, User } from 'components';
import { theme, toRem } from 'design';
import { DisbursementMethod, IPayout } from 'types';
import { centsToDollarsDisplay, centsToNegativeDollarsDisplay } from 'utils/currency';

const PaymentDescriptionLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(theme.space.md)};
  width: 100%;
  margin-top: ${toRem(theme.space.md)};
  word-break: break-word;

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-gap: ${toRem(theme.space.xl)};
    margin-top: 0;
  }
`;

const StyledRow = styled(Row)`
  cursor: pointer;

  * {
    cursor: pointer;
  }
`;

interface IPaymentDescriptionProps {
  description: string;
  isLoading?: boolean;
}

export const PaymentDescription = ({ description, isLoading, ...rest }: IPaymentDescriptionProps) => (
  <PaymentDescriptionLayout {...rest}>
    <Label>{isLoading ? <Skeleton width={150} /> : 'Description'}</Label>
    <Text>{isLoading ? <Skeleton width={250} /> : description}</Text>
  </PaymentDescriptionLayout>
);

interface IPayeeRowProps {
  payout: IPayout;
  isLoading?: boolean;
  onClick?: () => void;
  siteUsesHoldback?: boolean;
  className?: string;
}

export const PayeeRow = ({ payout, isLoading, onClick, siteUsesHoldback, ...rest }: IPayeeRowProps) => (
  <StyledRow onClick={onClick} {...rest}>
    <Cell wrapText minWidth={150} maxWidth={400} data-label="Payee">
      <User id={payout.id} username={payout.employee.name} userId={payout.userId} hasPaymentMethod={false} isDismissed={!!payout.employee.deletedAt} isLoading={isLoading} stackable={false} details={payout.id} />
    </Cell>

    <Cell minWidth={150} data-label="Status">
      <StatusPayment data-cy="status" status={payout.status} subStatusLabel={payout.alert?.label} subStatusTooltip={payout.alert?.message} isLoading={isLoading} paymentId={payout.id} />
    </Cell>

    <Cell data-label="Tips" align="left" data-cy="tips">
      {isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(payout.gross - payout.autoGratuity)}
    </Cell>

    <Cell data-label="Autograts" align="left" data-cy="autograts">
      {isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(payout.autoGratuity)}
    </Cell>

    {siteUsesHoldback && (
      <>
        <Cell data-label="Gross" align="left" data-cy="gross">
          {isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(payout.gross)}
        </Cell>
        <Cell data-label="Holdback" align="left" data-cy="holdback">
          {isLoading ? <Skeleton width={50} /> : payout.disbursementMethod === DisbursementMethod.CASH ? '--' : centsToNegativeDollarsDisplay(payout.holdback)}
        </Cell>
      </>
    )}

    <Cell data-label="Paid" align="left" data-cy="paid">
      <strong>{isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(payout.net)}</strong>
    </Cell>
  </StyledRow>
);
